import { lookupByAirportCode } from './airport_code_aliases.js';
import { AIRPORT_TIMEZONES } from './airport_timezones.js';

/**
 * Geo reference: resolve an airport's IANA timezone and render a UTC instant as
 * the airport's LOCAL wall-clock.
 *
 * Why this exists: a flight datetime carries NO timezone field — only the IATA
 * code per segment. To display the wall-clock a traveler actually experiences we
 * must resolve the departure/arrival airport's zone. The voucher gives us the
 * IATA; this module supplies IATA→IANA and both directions of the conversion.
 *
 * `airportLocalIso` converts a **true UTC** instant (Cirium's, ours) into that
 * wall-clock. The VOUCHER producer is NOT such a source — see
 * {@link flightSourceTimes}, which is what a voucher datetime must go through.
 *
 * Pure (no IO): the IATA→IANA table is a compile-time import and `Intl` is the
 * platform's ICU — DST is handled correctly for international zones.
 */

// The two shapes the source writes for an airport: the code followed by a
// separator and descriptive text ("FLN - FLORIANOPOLIS", "GRU-SAO PAULO",
// "SCL - SANTIAGO, CL"), or a bare upper-case code ("FLN").
//
// A code followed by a SPACE and more letters is neither: it is the first word
// of a street address or a city name, and `journey.origin` carries exactly that
// for a hotel or a mobility ride. "Rua Italia, 180" starts with RUA (Arua,
// Uganda), "Araçatuba" with ARA (Acadiana, Louisiana) — matching on `\b` put
// thousands of vouchers in a foreign zone. The bare form is upper-case only for
// the same reason: "Gas" is a word, GAS is Garissa — an all-lower-case "gru" is
// still a code (#7058): no address or city is three lower-case letters alone.
// A code in parentheses ("Lisboa (LIS)", "(LIS) Lisboa") is upper-case only, for
// the same reason as the bare form.
const PREFIXED_IATA = /^\s*([A-Za-z]{3})\s*[-–—/(,]/;
const BARE_IATA = /^\s*([A-Z]{3}|[a-z]{3})\s*$/;
const BRACKETED_IATA = /^\s*\(\s*([A-Z]{3})\s*\)|\(\s*([A-Z]{3})\s*\)\s*$/;

/**
 * Hand corrections on top of the GENERATED table (mwgg/Airports), which files a
 * few Brazilian border airports under the neighbour's zone and misses some
 * regional ones. Audited 2026-09-26 against the `timeZoneRegionName` Cirium ships
 * in every Flex callback appendix (181/184 flown airports agree by offset; see
 * #3756). A wrong zone here is a wrong HOUR in every flight message for that
 * airport — PMG as Asunción put OF-F17677413 one hour off after the #3373 fix —
 * and a wrong `isBrazilianAirport` (IGU as Córdoba and TBT as Bogotá read as foreign
 * with the right offset). A regional the dataset lists by ICAO alone (no IATA, so the
 * generator skips it) takes the zone of that ICAO row.
 * Regenerating the table never touches this map.
 */
const AIRPORT_TIMEZONE_OVERRIDES: Readonly<Record<string, string>> = {
  EEA: 'America/Sao_Paulo', // Correia Pinto (SC) — absent from the dataset
  IGU: 'America/Sao_Paulo', // Foz do Iguaçu (PR) — dataset: America/Argentina/Cordoba
  JJG: 'America/Sao_Paulo', // Jaguaruna (SC) — absent
  JMA: 'America/Sao_Paulo', // Manhuaçu (MG) — ICAO only (SNJM)
  JPO: 'America/Fortaleza', // Patos (PB) — ICAO only (SNTS)
  LHN: 'America/Sao_Paulo', // Linhares (ES) — ICAO only (SNLN)
  PMG: 'America/Campo_Grande', // Ponta Porã (MS) — dataset: America/Asuncion
  PYT: 'America/Sao_Paulo', // Paracatu (MG) — ICAO only (SNZR)
  RRJ: 'America/Sao_Paulo', // Rio de Janeiro, Jacarepaguá — ICAO only (SBJR)
  SMT: 'America/Cuiaba', // Sorriso (MT) — absent
  TBT: 'America/Eirunepe', // Tabatinga (AM, UTC−5 by Lei 12.876/2013) — dataset: America/Bogota (right offset, not Brazilian)
};

/**
 * The IANA timezone for an airport, or `null` when the code is unknown/absent.
 * Accepts either a bare IATA code (`"FLN"`) or the source's IATA-prefixed
 * display string (`"FLN - FLORIANOPOLIS"`); free text that merely BEGINS with
 * three letters (a street address, a city name) is not an airport.
 */
export function airportTimezone(airport: string | null | undefined): string | null {
  if (!airport) return null;
  const bracketed = BRACKETED_IATA.exec(airport);
  const code =
    PREFIXED_IATA.exec(airport)?.[1] ??
    BARE_IATA.exec(airport)?.[1] ??
    bracketed?.[1] ??
    bracketed?.[2];
  if (!code) return null;
  return lookupByAirportCode(
    code.toUpperCase(),
    (iata) => AIRPORT_TIMEZONE_OVERRIDES[iata] ?? AIRPORT_TIMEZONES[iata],
  );
}

// One formatter per zone — Intl.DateTimeFormat construction is the expensive
// part, so cache it. `h23` keeps midnight as `00`, not the `24` some engines
// emit under `hour12: false`.
const formatters = new Map<string, Intl.DateTimeFormat>();
function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/**
 * IANA zones that exist only inside Brazil. The airport reference has no
 * country column, and IANA zones are country-scoped, so "both airports sit in
 * a Brazilian zone" is the honest proxy for a domestic leg — the MCT rule's
 * only question. Unknown airports read as NOT Brazilian (conservative: the
 * caller falls back to the international minimum).
 */
const BRAZILIAN_ZONES: ReadonlySet<string> = new Set([
  'America/Araguaina',
  'America/Bahia',
  'America/Belem',
  'America/Boa_Vista',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Eirunepe',
  'America/Fortaleza',
  'America/Maceio',
  'America/Manaus',
  'America/Noronha',
  'America/Porto_Velho',
  'America/Recife',
  'America/Rio_Branco',
  'America/Santarem',
  'America/Sao_Paulo',
]);

export function isBrazilianAirport(airport: string | null | undefined): boolean {
  const zone = airportTimezone(airport);
  return zone !== null && BRAZILIAN_ZONES.has(zone);
}

/** Wall clock of `date` in `timeZone`, offset-less ISO — the body `airportLocalIso` always had. */
export function wallClockIn(timeZone: string, date: Date): string | null {
  const parts = new Map(
    zoneFormatter(timeZone)
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  const year = parts.get('year');
  const month = parts.get('month');
  const day = parts.get('day');
  const hour = parts.get('hour');
  const minute = parts.get('minute');
  const second = parts.get('second');
  if (!year || !month || !day || !hour || !minute || !second) return null;
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

/**
 * Render a UTC instant as the airport's local wall-clock, as an offset-less ISO
 * string `YYYY-MM-DDTHH:mm:ss` (no `Z`, no offset — it IS the local clock).
 * Returns `null` when the airport zone is unknown or the date is absent/invalid,
 * so callers fall back to their existing UTC rendering.
 *
 * The offset-less shape is deliberate: display consumers slice it (`.slice(0,10)`
 * for the day, `11..16` for `HH:mm`) exactly as they sliced the source's raw
 * date strings — so swapping a raw value for this one is a drop-in.
 */
export function airportLocalIso(
  instant: Date | string | null | undefined,
  airport: string | null | undefined,
): string | null {
  const timeZone = airportTimezone(airport);
  if (!timeZone || instant == null) return null;
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  return wallClockIn(timeZone, date);
}

/** Zone offset (ms, positive east of UTC) in force at `at`. */
function offsetMsAt(timeZone: string, at: Date): number {
  const local = wallClockIn(timeZone, at);
  if (local === null) return 0;
  return Date.parse(`${local}Z`) - at.getTime();
}

/**
 * The UTC instant at which `timeZone`'s wall clock reads `localIso` — the
 * inverse of {@link wallClockIn}. Two passes over the zone offset (read at the
 * naive guess, then re-read at the corrected instant) so a DST edge lands on the
 * right side. A DST spring-forward gap has no corresponding instant: the
 * computed one does not render back to `localIso`, and the round-trip check
 * rejects it (`null`) instead of returning an instant an hour off. `null` too
 * for an unparseable `localIso` or an unknown zone.
 */
export function zoneUtcFromLocal(localIso: string, timeZone: string): string | null {
  const wall = Date.parse(`${localIso}Z`);
  if (Number.isNaN(wall)) return null;
  try {
    const firstGuess = wall - offsetMsAt(timeZone, new Date(wall));
    const utc = wall - offsetMsAt(timeZone, new Date(firstGuess));
    if (wallClockIn(timeZone, new Date(utc)) !== new Date(wall).toISOString().slice(0, 19)) {
      return null;
    }
    return new Date(utc).toISOString();
  } catch {
    return null;
  }
}

/** Inverse of `airportLocalIso`: the UTC instant at which the airport's wall
 *  clock reads `localIso` (see {@link zoneUtcFromLocal}). */
export function airportUtcFromLocal(
  localIso: string,
  airport: string | null | undefined,
): string | null {
  const timeZone = airportTimezone(airport);
  return timeZone ? zoneUtcFromLocal(localIso, timeZone) : null;
}

/**
 * The zone the voucher producer FRAMES flight datetimes in. It writes the
 * airport's wall clock with Brazil's offset glued on (`14:30` at SFO becomes
 * `17:30Z`), so a source instant is only a real instant for an airport that is
 * itself UTC-3. See {@link flightSourceTimes}.
 */
const SOURCE_FRAMING_ZONE = 'America/Sao_Paulo';

/** The instant the voucher source stores for a local wall clock: `localIso` framed at
 *  {@link SOURCE_FRAMING_ZONE}, the inverse of what {@link flightSourceTimes} reads as
 *  `localIso`. Lets a copy of the RAW source value be matched to its local twin. */
export function sourceFramedInstant(localIso: string): string | null {
  return zoneUtcFromLocal(localIso, SOURCE_FRAMING_ZONE);
}

/** The wall clock the voucher source wrote, read back off its -03:00-framed instant — the
 *  time on the ticket, whatever the airport's zone. Not an instant: for an airport of unknown
 *  zone it is the only honest reading left (a floating calendar time, #7058). */
export function sourceWallClock(framed: Date): string | null {
  return wallClockIn(SOURCE_FRAMING_ZONE, framed);
}

/**
 * Real elapsed minutes of a flight, from its two instants (#7058): the source's `duration` is
 * the difference of the two airport wall clocks read as one zone — MAO 02:10 (UTC-4) → CNF 06:45
 * (UTC-3) came out 4h35 for a 3h35 flight. The instants are real only where the airport zone is
 * known, which is exactly when the mapper set the local twin; without both ends the duration
 * would be off by hours, so it is unknown (`null`), never a wrong one.
 */
export function flightElapsedMinutes(leg: {
  departAt: Date | null | undefined;
  arriveAt: Date | null | undefined;
  departLocal?: string | null | undefined;
  arriveLocal?: string | null | undefined;
}): number | null {
  if (!leg.departLocal || !leg.arriveLocal || !leg.departAt || !leg.arriveAt) return null;
  const minutes = Math.round((leg.arriveAt.getTime() - leg.departAt.getTime()) / 60_000);
  return minutes > 0 ? minutes : null;
}

/** The two readings of one voucher flight datetime — see {@link flightSourceTimes}. */
export interface FlightSourceTimes {
  /** Wall clock at the airport, offset-less ISO. Null when the zone is unknown. */
  localIso: string | null;
  /** The real instant the wall clock names. Falls back to the raw source value. */
  utcInstant: Date | null;
}

/**
 * Read ONE voucher flight datetime, for the departure or arrival airport it
 * belongs to. The only sanctioned way to read a flight datetime off the voucher
 * source — do not hand such a value to `airportLocalIso`.
 *
 * The producer does NOT store true UTC: it stores the airport's LOCAL wall clock
 * framed at Brazil's -03:00. Delta DL 0689 SFO→LAX on 2026-09-24 departs 14:30
 * PDT (= 21:30Z) and the source carries `17:30Z` — 14:30 with `-03:00` glued on.
 * The legacy "Airline tickets" document prints that instant in a FIXED
 * `America/Sao_Paulo` and has always shown the right 2:30 PM, which is the same
 * evidence read from the other side.
 *
 * The premise this replaces ("the source stores true UTC", checked against Azul
 * AD2967 FLN→CNF `08:35Z` → 05:35) was verified only on a Brazilian DOMESTIC
 * leg, where the two readings are numerically identical and therefore cannot be
 * told apart. Treating the framed value as UTC and converting it to the airport
 * zone converts it twice, off by exactly `offset(airport) - (-3h)`: zero across
 * UTC-3 Brazil, -4h at SFO/LAX, +4h at Lisbon.
 *
 * Unknown airport zone, or a date the source wrote unparseably → the raw value
 * passes through and there is no local twin. Never invent one. A wall clock the
 * airport never strikes (a DST spring-forward gap) keeps the clock — it is what
 * the source says and what the traveler is shown — and the raw instant with it.
 */
export function flightSourceTimes(
  instant: Date | string | null | undefined,
  airport: string | null | undefined,
): FlightSourceTimes {
  if (instant == null) return { localIso: null, utcInstant: null };
  const framed = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(framed.getTime())) return { localIso: null, utcInstant: framed };
  if (airportTimezone(airport) === null) return { localIso: null, utcInstant: framed };
  const localIso = sourceWallClock(framed);
  if (localIso === null) return { localIso: null, utcInstant: framed };
  const utc = airportUtcFromLocal(localIso, airport);
  return { localIso, utcInstant: utc === null ? framed : new Date(utc) };
}

/**
 * Read ONE voucher MOBILITY ride datetime (`journey.dateStart`/`dateEnd`). Unlike a
 * flight, the ride carries the TRUE instant — an immediate ride's `dateStart`
 * equals its `createdAt` to the millisecond — and its place is a street address,
 * which names no zone. The local twin is Brazil's wall clock of that instant:
 * without it the 04:34 ride of #5205 was printed in UTC, 07:34.
 *
 * Known limit: a ride outside Brazil shows Brazil's clock — its real zone is not
 * knowable from an address — which still beats UTC. A HOTEL spine must NOT come
 * here: it stores the wall clock with `Z` glued on (`checkinHour 14:00` ↔
 * `…T14:00:00Z`), so this reading would move every check-in to 11:00.
 */
export function rideSourceTimes(instant: Date | string | null | undefined): FlightSourceTimes {
  if (instant == null) return { localIso: null, utcInstant: null };
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime())) return { localIso: null, utcInstant: at };
  return { localIso: wallClockIn(SOURCE_FRAMING_ZONE, at), utcInstant: at };
}

/**
 * Read ONE datetime of a SCHEDULED ride (`journey.provider` MOBILITY_SCHEDULE). Unlike the
 * immediate ride of {@link rideSourceTimes}, the schedule goes out as a naive local clock
 * (the legacy app's `DateTime.toIso8601String()` and the agent alike) and the source stores
 * it with `Z` glued on, like a hotel check-in: the car booked for 10:00 at GIG arrives as
 * `10:00Z`. Read as a true instant it printed 07:00. Same known limit: Brazil's clock.
 */
export function scheduledRideSourceTimes(
  instant: Date | string | null | undefined,
): FlightSourceTimes {
  if (instant == null) return { localIso: null, utcInstant: null };
  const framed = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(framed.getTime())) return { localIso: null, utcInstant: framed };
  const localIso = framed.toISOString().slice(0, 19);
  const utc = zoneUtcFromLocal(localIso, SOURCE_FRAMING_ZONE);
  return { localIso, utcInstant: utc === null ? framed : new Date(utc) };
}
